import React, { useCallback, useMemo, useState } from 'react';
import { Dimension } from '../BuyForm/BuyForm.types';
import { DIM_PREFIX } from '../BuyForm/useBuyForm';
import { SwatchColorGrid } from '@apps/registry/common/components/SwatchColorGrid';
import { ProductVariantFormField } from '../ProductVariantFormField';
import { SwatchColorCarousel } from '@apps/registry/common/components/SwatchColorCarousel';
import { ProductVariantStringList } from '../ProductVariantStringList';
import { GroupDimensionSwatchType } from '@graphql/generated';
import { SwatchColorOption } from '@apps/registry/common/components/SwatchColorGrid/types';
import { FieldInputProps } from 'formik';

interface BuyFormDimensionCommonProps
  extends Readonly<{
    colorSwatchVariant: 'grid' | 'carousel';
    showAllDimensions: boolean;
    isQuickAddForm: boolean | undefined;
    getFieldProps: (nameOrOptions: string | undefined) => FieldInputProps<string | undefined>;
    setDimensionOption: (optionValue: string, updatedField: string) => void;
    checkOptionAvailability: (optionId: string, dimensionId: string | undefined) => boolean;
    handleHover: (optionValue: string, updatedField: string, isHoverExit?: boolean) => void;
  }> {}

interface BuyFormDimensionsListProps
  extends BuyFormDimensionCommonProps,
    Readonly<{
      dimensions: Dimension[];
    }> {}

export const BuyFormDimensionsList: React.FC<BuyFormDimensionsListProps> = ({
  dimensions,
  colorSwatchVariant,
  showAllDimensions,
  isQuickAddForm,
  getFieldProps,
  setDimensionOption,
  checkOptionAvailability,
  handleHover,
}) => {
  return (
    <>
      {dimensions.map(dimension => (
        <BuyFormDimension
          key={dimension.id}
          dimension={dimension}
          colorSwatchVariant={colorSwatchVariant}
          showAllDimensions={showAllDimensions}
          isQuickAddForm={isQuickAddForm}
          getFieldProps={getFieldProps}
          setDimensionOption={setDimensionOption}
          checkOptionAvailability={checkOptionAvailability}
          handleHover={handleHover}
        />
      ))}
    </>
  );
};

interface BuyFormDimensionProps
  extends BuyFormDimensionCommonProps,
    Readonly<{
      dimension: Dimension;
    }> {}

const BuyFormDimension: React.FC<BuyFormDimensionProps> = ({
  dimension,
  colorSwatchVariant,
  showAllDimensions,
  isQuickAddForm,
  getFieldProps,
  setDimensionOption,
  checkOptionAvailability,
  handleHover,
}) => {
  const selectedOptionId = getFieldProps(`${DIM_PREFIX}${dimension.id}`).value;
  const selectedOptionLabel = useMemo(() => dimension.options.find(option => option.id === selectedOptionId)?.label, [dimension.options, selectedOptionId]);
  const [optionLabel, setOptionLabel] = useState(selectedOptionLabel);
  const handleOptionHover = useCallback((e: React.MouseEvent, swatch: SwatchColorOption) => {
    setOptionLabel(swatch.label);
  }, []);
  const handleOptionOut = useCallback(
    (e: React.MouseEvent, swatch: SwatchColorOption) => {
      setOptionLabel(selectedOptionLabel);
    },
    [selectedOptionLabel],
  );

  if (dimension.swatchType === GroupDimensionSwatchType.color || dimension.swatchType === GroupDimensionSwatchType.image) {
    if (dimension.options?.length > 1) {
      return (
        <ProductVariantFormField
          key={dimension.label}
          label={`${dimension.label}:`}
          helperText={optionLabel}
          error={false}
        >
          {colorSwatchVariant === 'grid' ? (
            <SwatchColorGrid
              id={dimension.id}
              name={dimension.label}
              value={selectedOptionId}
              columns={6}
              swatchSize={8}
              options={dimension.options}
              onInteract={(e: React.SyntheticEvent, color: SwatchColorOption) => setDimensionOption(color.id, dimension.id)}
              interactionType={'click'}
              disableOptionOverflow={showAllDimensions}
              checkAvailability={true}
              checkOptionAvailability={checkOptionAvailability}
              onSwatchHover={(e, color: SwatchColorOption) => handleHover(color.id, dimension.id, false)}
              onSwatchHoverExit={(e, color: SwatchColorOption) => handleHover(color.id, dimension.id, true)}
            />
          ) : (
            <SwatchColorCarousel
              id={dimension.id}
              name={dimension.label}
              value={selectedOptionId}
              swatchSize={24}
              options={dimension.options}
              onInteract={(color: SwatchColorOption) => setDimensionOption(color.id, dimension.id)}
              onSwatchHover={handleOptionHover}
              onSwatchOut={handleOptionOut}
              interactionType={'click'}
              checkAvailability={true}
              checkOptionAvailability={checkOptionAvailability}
            />
          )}
        </ProductVariantFormField>
      );
    }
    return null;
  }

  return (
    <ProductVariantFormField
      key={dimension.label}
      label={`${dimension.label}:`}
      helperText={optionLabel}
      error={false}
    >
      <ProductVariantStringList
        id={dimension.id}
        name={dimension.label}
        options={dimension.options}
        value={selectedOptionId}
        variant="pill"
        onChange={dimensionOptionId => {
          setDimensionOption(dimensionOptionId ?? '', dimension.id);
        }}
        checkAvailability={true}
        isQuickAddForm={isQuickAddForm}
        checkOptionAvailability={checkOptionAvailability}
        size={'sm'}
      />
    </ProductVariantFormField>
  );
};
